"use client";

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { User, LogOut, LayoutDashboard } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { logoutAndRedirect } from '@/lib/auth-utils';
import LoadingSpinner from '@/components/app/loading/LoadingSpinner';
import type { AuthState } from '@/lib/types/auth';

interface NavbarAuthProps {
  initialAuth: AuthState;
  inMobileMenu?: boolean;
}

/**
 * NavbarAuth - Modern authentication component for navbar
 * Uses server-provided auth state for optimal performance while preserving
 * all original business logic, styling, and user experience
 * 
 * 2025 SSR-Safe Implementation:
 * - No window.innerWidth access during initial render
 * - Uses mounted state to prevent hydration mismatches 
 * - Defers responsive state updates to client-side only
 */
export default function NavbarAuth({ 
  initialAuth,
  inMobileMenu = false 
}: NavbarAuthProps) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  
  // ✅ SSR-safe state management following 2025 best practices
  const [isMounted, setIsMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false); // SSR-safe default
  
  // ✅ Use server-provided auth state (eliminates client-side API calls)
  const authStatus = initialAuth.isAuthenticated ? 'authenticated' : 'unauthenticated';

  // ✅ SSR-safe responsive detection (2025 standard)
  useEffect(() => {
    // Mark component as mounted
    setIsMounted(true);
    
    // Only access window after mount to prevent hydration mismatch
    const updateMobileState = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Set initial state after mount
    updateMobileState();
    
    // Add resize listener for responsive updates
    window.addEventListener('resize', updateMobileState);
    
    return () => {
      window.removeEventListener('resize', updateMobileState);
    };
  }, []);

  // Reset loading state after navigation attempts (fallback)
  useEffect(() => {
    if (isNavigating) {
      const timer = setTimeout(() => {
        setIsNavigating(false);
      }, 3000); // 3 second fallback timeout
      
      return () => clearTimeout(timer);
    }
  }, [isNavigating]);

  const shouldUseMobileStyles = isMounted && isMobile;
  
  const handleLogout = async () => {
    setIsLoading(true);
    
    // Set up a safety timeout to reset loading state if logout takes too long
    const safetyTimeout = setTimeout(() => {
      console.warn('NavbarAuth logout taking longer than expected, resetting component state');
      setIsLoading(false);
    }, 12000); // 12 second safety timeout
    
    try {
      await logoutAndRedirect();
      // logoutAndRedirect now handles all success/error feedback and redirects
    } catch (error) {
      console.error("NavbarAuth: Error during logout:", error);
      // Error handling is now done in logoutAndRedirect with proper user feedback
      setIsLoading(false);
    } finally {
      // Clear the safety timeout
      clearTimeout(safetyTimeout);
    }
  };

  // Handle navigation with loading state
  const handleNavigation = (href: string) => {
    setIsNavigating(true);
    if (href === "/dashboard") {
      window.location.href = href;
    } else {
      router.push(href);
    }
  };
  
  if (isLoading) {
    return (
      <Button
        variant="default"
        className={`transition-all duration-300 hover:shadow-md transform-gpu hover:scale-105 opacity-70 ${
          shouldUseMobileStyles || inMobileMenu ? 'rounded-lg px-4 py-1.5 text-sm w-full' : 'rounded-full px-6 py-2'
        }`}
        disabled={true}
      >
        <span className="h-4 w-4 mr-2 inline-block border-t-2 border-r-2 border-white rounded-full animate-spin"></span>
        Loading...
      </Button>
    );
  }
  
  if (authStatus === 'authenticated') {
    if (inMobileMenu) {
      return (
        <>
          {/* Global loading overlay */}
          {isNavigating && <LoadingSpinner />}
          
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex gap-3">
            <Button 
              onClick={() => handleNavigation("/dashboard")}
              variant="default"
              className="flex-1 transition-all duration-300 hover:shadow-md rounded-lg"
              disabled={isNavigating}
            >
              <LayoutDashboard className="h-4 w-4" />
              {isNavigating ? "Loading..." : "Account"}
            </Button>
            <Button
              onClick={handleLogout}
              variant="danger"
              className={`flex-1 transition-all duration-300 rounded-lg ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
              disabled={isLoading || isNavigating}
            >
              <LogOut className="h-4 w-4 mr-2" />
              {isLoading ? "Signing Out..." : "Sign Out"}
            </Button>
          </div>
        </div>
        </>
      );
    }

    return (
      <>
        {/* Global loading overlay */}
        {isNavigating && <LoadingSpinner />}
        
        <Button 
          onClick={() => handleNavigation("/dashboard")}
          variant="default"
          className={`transition-all duration-300 hover:shadow-md transform-gpu hover:scale-105 ${
            shouldUseMobileStyles ? 'rounded-lg px-4 py-1.5 text-sm' : 'rounded-full px-6 py-2'
          } ${isNavigating ? 'opacity-70 cursor-not-allowed' : ''}`}
          disabled={isNavigating}
        >
          <LayoutDashboard className="h-4 w-4" />
          {isNavigating ? "Loading..." : (shouldUseMobileStyles ? "Account" : "Dashboard")}
        </Button>
      </>
    );
  }
  
  if (inMobileMenu) {
    return (
      <>
        {/* Global loading overlay */}
        {isNavigating && <LoadingSpinner />}
        
        <div className="bg-gray-50 rounded-xl p-4">
          <h3 className="text-center text-gray-800 font-medium mb-3">
            Login to access all our unique features.
          </h3>
        <div className="flex gap-3">
          <Button 
            onClick={() => handleNavigation("/register")}
            variant="secondary"
            className={`flex-1 transition-all duration-300 rounded-lg ${isNavigating ? 'opacity-70 cursor-not-allowed' : ''}`}
            disabled={isNavigating}
          >
            {isNavigating ? "Loading..." : "Sign Up"}
          </Button>
          <Button 
            onClick={() => handleNavigation("/login")}
            variant="default"
            className={`flex-1 transition-all duration-300 rounded-lg ${isNavigating ? 'opacity-70 cursor-not-allowed' : ''}`}
            disabled={isNavigating}
          >
            <User className="h-4 w-4 mr-2" />
            {isNavigating ? "Loading..." : "Sign In"}
          </Button>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      {/* Global loading overlay */}
      {isNavigating && <LoadingSpinner />}
      
      <Button 
        onClick={() => handleNavigation("/login")}
        variant="default"
        className={`transition-all duration-300 hover:shadow-md transform-gpu hover:scale-105 ${
          shouldUseMobileStyles ? 'rounded-lg px-4 py-1.5 text-sm' : 'rounded-full px-6 py-2'
        } ${isNavigating ? 'opacity-70 cursor-not-allowed' : ''}`}
        disabled={isNavigating}
      >
        <User className="h-4 w-4 mr-2" />
        {isNavigating ? "Loading..." : "Sign In"}
      </Button>
    </>
  );
} 